import React, { useState, useEffect } from "react";
import { supabase } from "../../supabaseClient";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext.js";
import { useOrganization } from "../../context/OrganizationContext";
import { FormFieldsSkeleton } from "../UI/DesignStandards";
import TournamentInputs from "./TournamentInputs";
import HandicapInputs from "./HandicapInputs";
import { sanitizeText, sanitizeDescription } from "../../utils/sanitization";
import {
  PrimaryButton,
  SecondaryButton,
} from "../UI/DesignStandards";
import InlineAlert from "../UI/DesignStandards/Notifications/InlineAlert";
import { emptyTournamentForm } from "../../utility/houseTournamentDefaults";
import { getAdminTabUrl } from "../../utility/adminNavigation";
import { handicapFormErrors } from "../../utility/handicapFieldValidation";
import { handicapValuesForSave } from "../../utility/tournamentScoringType";
import { requestTournamentIndexNow } from "../../utility/requestIndexNow";
import {
  getOrganizationTournamentCap,
  isTournamentCapError,
  tournamentAtCapMessage,
  tournamentCapUsageCopy,
} from "../../utility/tournamentCaps";

// Helper function to check if a role has a specific permission
const hasPermission = (role, permission) => {
  if (!role.permissions) return false;

  // Handle string permissions (comma-separated)
  if (typeof role.permissions === "string") {
    return role.permissions.split(",").includes(permission);
  }

  // Handle array permissions
  if (Array.isArray(role.permissions)) {
    return role.permissions.includes(permission);
  }

  // Handle object permissions
  if (typeof role.permissions === "object") {
    return role.permissions[permission] === true;
  }

  return false;
};

const CreateTournamentForm = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { currentOrganization } = useOrganization();
  const [loading, setLoading] = useState(true);
  const [organizations, setOrganizations] = useState([]);
  const [organizationalID, setOrganizationalID] = useState(null);
  const [tournamentDetails, setTournamentDetails] = useState(emptyTournamentForm);
  const [errorMessages, setErrorMessages] = useState({ global: "" });
  const [capStatus, setCapStatus] = useState(null);

  useEffect(() => {
    if (!organizationalID) {
      setCapStatus(null);
      return undefined;
    }
    let cancelled = false;
    getOrganizationTournamentCap(organizationalID)
      .then((next) => {
        if (!cancelled) setCapStatus(next);
      })
      .catch(() => {
        if (!cancelled) setCapStatus(null);
      });
    return () => {
      cancelled = true;
    };
  }, [organizationalID]);

  useEffect(() => {
    const fetchUserOrganizations = async () => {
      if (user) {
        setLoading(true);
        try {
          // Use the same approach as OrganizationContext - direct query instead of RPC
          const { data: memberData, error: memberError } = await supabase
            .from("organizationMembers")
            .select(
              `
              id,
              organizationID,
              status,
              memberRoles:organizationMemberRoles (
                id,
                roleID,
                isDeleted,
                role:organizationRoles (
                  id,
                  name,
                  description,
                  permissions,
                  rank
                )
              ),
              organization:organizations (
                id,
                name,
                nameAbbreviation,
                description
              )
            `
            )
            .eq("userID", user.id)
            .eq("status", "active");

          if (memberError) {
            console.error("Error fetching organization members:", memberError);
            console.error("Error details:", {
              code: memberError.code,
              message: memberError.message,
              details: memberError.details,
              hint: memberError.hint,
            });

            // If the error suggests the organizations table doesn't exist, try a different approach
            if (
              memberError.message.includes("organizations") ||
              memberError.code === "42P01"
            ) {
              setErrorMessages((prev) => ({
                ...prev,
                global:
                  "You don't have tournament management permissions for any organizations. Please contact an organization admin to get access.",
              }));
              return;
            }

            setErrorMessages((prev) => ({
              ...prev,
              global: `Error fetching organizations: ${memberError.message}. Please check if the organizations table exists.`,
            }));
            return;
          }

          if (memberData && memberData.length > 0) {
            // Filter members who have manage_tournaments permission
            const adminMembers = memberData.filter((member) => {
              const activeRoles =
                member.memberRoles?.filter((mr) => !mr.isDeleted) || [];
              return activeRoles.some((mr) =>
                hasPermission(mr.role, "manage_tournaments")
              );
            });

            // Extract organization data from admin member records
            const orgs = adminMembers
              .map((member) => member.organization)
              .filter(Boolean); // Remove any null organizations

            if (orgs.length > 0) {
              setOrganizations(orgs);

              const preferredId =
                currentOrganization?.id &&
                orgs.some((org) => org.id === currentOrganization.id)
                  ? currentOrganization.id
                  : orgs[0].id;
              if (!organizationalID) {
                setOrganizationalID(preferredId);
              }
            } else {
              // No organizations with tournament management permissions found
              setOrganizations([]);
              setErrorMessages((prev) => ({
                ...prev,
                global:
                  "You don't have tournament management permissions for any organizations. Please contact an organization admin to get access.",
              }));
            }
          } else {
            // No organization members found
            setOrganizations([]);
            setErrorMessages((prev) => ({
              ...prev,
              global:
                "You don't have tournament management permissions for any organizations. Please contact an organization admin to get access.",
            }));
          }
        } catch (error) {
          console.error("Unexpected error fetching organizations:", error);
          setErrorMessages((prev) => ({
            ...prev,
            global: `Unexpected error: ${error.message}`,
          }));
        } finally {
          setLoading(false);
        }
      }
    };

    fetchUserOrganizations();
  }, [user]); // Removed organizationalID from dependency array

  const validatePayoutRatio = (numerator, denominator) => {
    if (!numerator || !denominator) return "Payout ratio is required";
    if (numerator <= 0 || denominator <= 0) {
      return "Both numbers must be greater than 0";
    }
    return "";
  };

  const handleCreateTournament = async () => {
    setLoading(true);
    let errors = {
      name: "",
      handicapBase: "",
      handicapPercentage: "",
      handicapMax: "",
      startDate: "",
      payoutRatio: "",
      organization: "",
    };

    // Check if user has organizations
    if (organizations.length === 0) {
      setErrorMessages((prev) => ({
        ...prev,
        global:
          "You don't have tournament management permissions for any organizations. Please contact an organization admin to get access.",
      }));
      setLoading(false);
      return;
    }

    let nextCap = capStatus;
    try {
      nextCap = await getOrganizationTournamentCap(organizationalID);
      setCapStatus(nextCap);
    } catch (_) {
      // Keep the last loaded cap status when a refresh fails.
    }
    const capCopy = tournamentCapUsageCopy(nextCap || {});
    if (capCopy.atCap) {
      setErrorMessages((prev) => ({
        ...prev,
        global: capCopy.message,
      }));
      setLoading(false);
      return;
    }

    // Validation checks
    if (!tournamentDetails.name) errors.name = "Tournament name is required.";
    if (!tournamentDetails.startDate)
      errors.startDate = "Start Date is required.";
    if (!organizationalID)
      errors.organization = "Please select an organization.";
    Object.assign(
      errors,
      handicapFormErrors({
        scoringType: tournamentDetails.scoringType,
        handicapBase: tournamentDetails.handicapBase,
        handicapPercentage: tournamentDetails.handicapPercentage,
        handicapMax: tournamentDetails.handicapMax,
      })
    );

    // Validate payout ratio
    const payoutRatioError = validatePayoutRatio(
      tournamentDetails.payoutRatioNumerator,
      tournamentDetails.payoutRatioDenominator
    );
    if (payoutRatioError) {
      errors.payoutRatio = payoutRatioError;
    }

    setErrorMessages(errors);
    if (Object.values(errors).some((error) => error)) {
      setLoading(false);
      return;
    }

    // Parse the payout ratio
    const [numerator, denominator] = [
      Number(tournamentDetails.payoutRatioNumerator),
      Number(tournamentDetails.payoutRatioDenominator),
    ];

    try {
      const { data: org, error: orgError } = await supabase
        .from("organizations")
        .select("defaultPayoutTableId")
        .eq("id", organizationalID)
        .single();

      if (orgError) {
        console.error("Supabase error:", orgError);
        setErrorMessages((prev) => ({
          ...prev,
          global: "Error creating tournament. Please try again.",
        }));
        setLoading(false);
        return;
      }

      const { data, error } = await supabase
        .from("tournaments")
        .insert({
          name: sanitizeText(tournamentDetails.name),
          description: tournamentDetails.description
            ? sanitizeDescription(tournamentDetails.description)
            : null,
          ...handicapValuesForSave(tournamentDetails),
          startDate: tournamentDetails.startDate,
          organizationID: organizationalID,
          payoutRatioNumerator: numerator,
          payoutRatioDenominator: denominator,
          payoutTableId: org?.defaultPayoutTableId || null,
        })
        .select()
        .single();

      if (error) {
        console.error("Supabase error:", error);
        setErrorMessages((prev) => ({
          ...prev,
          global: isTournamentCapError(error)
            ? tournamentAtCapMessage(nextCap?.tournamentLimit)
            : "Error creating tournament. Please try again.",
        }));
        return;
      }

      setTournamentDetails(emptyTournamentForm());
      void requestTournamentIndexNow(data.id);
      navigate(getAdminTabUrl(data.slug || data.id, "setup"));
    } catch (err) {
      console.error("Unexpected error:", err);
      setErrorMessages((prev) => ({
        ...prev,
        global: "Unexpected error occurred. Please try again.",
      }));
    } finally {
      setLoading(false);
    }
  };

  const capCopy = tournamentCapUsageCopy(capStatus || {});

  return (
    <div className="card-board overflow-hidden">
      <div className="bg-brand-charcoal text-brand-cream px-6 py-4">
        <h1 className="font-display font-bold text-xl text-brand-cream tracking-tight">
          Create Tournament
        </h1>
        <p className="text-sm text-brand-mist mt-1">
          Name, date, handicap, and payout ratio.
        </p>
      </div>

      <div className="p-6">
        {errorMessages.global && (
          <div className="mb-6 space-y-2">
            <InlineAlert
              type="error"
              title="There was a problem"
              message={errorMessages.global}
            />
            {/Open Plan/i.test(errorMessages.global) ? (
              <Link
                to="/organization/plan"
                className="inline-block text-sm underline text-brand-gold-ink"
              >
                Open Plan
              </Link>
            ) : null}
          </div>
        )}

        {!errorMessages.global && capCopy.message ? (
          <div className="mb-6 space-y-2">
            <InlineAlert
              type={capCopy.atCap ? "error" : "warning"}
              title="Tournament limit"
              message={capCopy.message}
            />
            <Link
              to="/organization/plan"
              className="inline-block text-sm underline text-brand-gold-ink"
            >
              Open Plan
            </Link>
          </div>
        ) : null}

        {loading ? (
          <FormFieldsSkeleton fields={6} />
        ) : (
          <form className="space-y-6">
            <div>
              <h2 className="heading-display text-lg mb-4">
                Basic Information
              </h2>
              <TournamentInputs
                tournamentDetails={tournamentDetails}
                setTournamentDetails={setTournamentDetails}
                organizations={organizations}
                organizationalID={organizationalID}
                setOrganizationalID={setOrganizationalID}
                errorMessages={errorMessages}
              />
            </div>

            <div>
              <h2 className="heading-display text-lg mb-4">Scoring</h2>
              <HandicapInputs
                scoringType={tournamentDetails.scoringType}
                setScoringType={(value) =>
                  setTournamentDetails({
                    ...tournamentDetails,
                    scoringType: value,
                  })
                }
                handicapBase={tournamentDetails.handicapBase}
                setHandicapBase={(value) =>
                  setTournamentDetails({
                    ...tournamentDetails,
                    handicapBase: value,
                  })
                }
                handicapPercentage={tournamentDetails.handicapPercentage}
                setHandicapPercentage={(value) =>
                  setTournamentDetails({
                    ...tournamentDetails,
                    handicapPercentage: value,
                  })
                }
                handicapMax={tournamentDetails.handicapMax}
                setHandicapMax={(value) =>
                  setTournamentDetails({
                    ...tournamentDetails,
                    handicapMax: value,
                  })
                }
                errorMessages={errorMessages}
                scoringName="create-tournament-scoring"
              />
            </div>

            <div className="flex justify-end gap-2 pt-4 border-t border-brand-charcoal/10">
              <SecondaryButton
                type="button"
                onClick={() => navigate("/tournaments")}
                className="!px-3 !py-2 text-sm"
              >
                Cancel
              </SecondaryButton>
              <PrimaryButton
                type="button"
                onClick={handleCreateTournament}
                disabled={capCopy.atCap}
              >
                Create Tournament
              </PrimaryButton>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default CreateTournamentForm;
